import { useTranslation } from "react-i18next";
import { Fragment } from "react/jsx-runtime";
import { FaUserCircle } from "react-icons/fa";
import styles from "../../css/messages.module.css";
import useAuth from "../../../../hooks/useAuth";
import { useFiles } from "../../../../hooks/useFiles";
import { getDateTime } from "../../../../utils/dateTime";
import { BASE_URL } from "../../../../constants/apiConstants";

export const MessageList = ({
  displayUnreadCount,
  messages,
  getMemberName,
  setSelectedImage,
  setShowImageModal,
  unreadDividerRef,
  lastMessageRef,
}) => {
  const { auth } = useAuth();
  const { i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const { getFileIcon, formatFileSize, handleDownload } = useFiles();

  const getMessageDirection = (author) => {
    if (author.id === auth.id && author.type === auth.role) {
      return i18n.dir();
    }
    return isRTL ? "ltr" : "rtl";
  };

  // Calculate where to insert the unread divider - USE displayUnreadCount
  const unreadDividerIndex = displayUnreadCount > 0 
    ? messages.length - displayUnreadCount
    : -1;

  // Render message body - handles both text and files
  const renderMessageBody = (message) => {
    // Regular text message
    if (!message.file) {
      return renderTextMessage(message);
    } else if (message.file) {
      // if message has a file attachment
      const fileUrl = `${BASE_URL}${message.file.path}`;
      const fileName = message.file.name || "File";
      const fileType = message.file.type;
      const fileSize = message.file.size;

      if (fileType === "image") {
        return (
          <>
            {renderImagePreview(message)}
            {/* display text message if there */}
            {message.body && renderTextMessage(message)}
          </>
        );
      }

      return (
        <>
          {renderFilePreview(fileUrl, fileName, fileType, fileSize)}
          {/* display text message if there */}
          {message.body && renderTextMessage(message)}
        </>
      );
    }
  };

  const renderFilePreview = (fileUrl, fileName, fileType, fileSize) => {
    return (
      <div
        className={styles.fileMessageContainer}
        onClick={() => handleDownload(fileUrl, fileName)}
      >
        <div className={styles.fileMessageContent}>
          {getFileIcon(fileType, fileName)}
          <div className={styles.fileMessageInfo}>
            <span className={styles.fileMessageName}>{fileName}</span>
            {fileSize && (
              <span className={styles.fileMessageSize}>
                {formatFileSize(fileSize)}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render image preview if it's an image file
  const renderImagePreview = (message) => {
    const imageUrl = `${BASE_URL}${message.file.path}`;

    return (
      <img
        src={imageUrl}
        alt={message.file.name || "Image Message"}
        className={styles.messageImage}
        loading="lazy"
        onClick={() => {
          setSelectedImage(message.file);
          setShowImageModal(true);
        }}
        style={{ cursor: "pointer" }}
      />
    );
  };

  const renderTextMessage = (message) => {
    return (
      <p className={styles.chatMsg} dir={i18n.dir()}>
        {message.body}
      </p>
    );
  };

  return (
    <>
      {messages.map((message, index) => (
        <Fragment key={message.id}>
          {/* Insert unread divider before the first unread message */}
          {index === unreadDividerIndex && (
            <div ref={unreadDividerRef} className={styles.unreadDivider}>
              <div className={styles.unreadDividerLine} />
              <span className={styles.unreadDividerText}>
                New Messages
              </span>
              <div className={styles.unreadDividerLine} />
            </div>
          )}
          
          <div
            ref={index === messages.length - 1 ? lastMessageRef : null}
            className={styles.chatMsgContainer}
            dir={getMessageDirection(message.author)}
          >
            {!message.author?.image ? (
              <FaUserCircle color="#999" className={styles.chatMsgImg} />
            ) : (
              <img
                src={`${BASE_URL}${message.author?.image}`}
                alt="user"
                className={styles.chatMsgImg}
              />
            )}
            <div className={styles.chatMsgBox}>
              <p className={styles.chatMsgAuthorName}>
                {getMemberName(message.author)}
              </p>
              {renderMessageBody(message)}
              <p className={styles.chatMsgDate} dir={isRTL ? "ltr" : "rtl"}>
                {getDateTime(message.created_at, i18n.language)}
              </p>
            </div>
          </div>
        </Fragment>
      ))}
    </>
  );
};
